import { useCallback, useRef, useState } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ConfigResponse, ShareClient, ConfigState } from '../types.tsx';
import {
	CHANNEL_ID,
	shareConfigurationLoaded,
	shareConfigurationLoadFailed,
} from '../analytics.tsx';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const useShareConfig = (
	shareClient: ShareClient,
	cloudId: string,
): {
	config: ConfigState;
	fetchConfig: () => Promise<void>;
} => {
	const [config, setConfig] = useState<ConfigState>('idle');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const lastFetchedAtRef = useRef<number | null>(null);

	const fetchConfig = useCallback(async () => {
		const startTime = Date.now();
		// If cached and fresh (within 5 minutes), return it
		if (lastFetchedAtRef.current && Date.now() - lastFetchedAtRef.current < CACHE_TTL_MS) {
			return;
		}
		try {
			setConfig('fetching');
			const configResponse: ConfigResponse = await shareClient.getConfig(cloudId);

			createAnalyticsEvent(
				shareConfigurationLoaded({
					disableSharingToEmails: configResponse.disableSharingToEmails,
					durationMs: Date.now() - startTime,
				}),
			).fire(CHANNEL_ID);

			lastFetchedAtRef.current = Date.now();
			setConfig(configResponse);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setConfig('error');

			createAnalyticsEvent(
				shareConfigurationLoadFailed({
					statusCode: error.code || error.status || error.statusCode,
					durationMs: Date.now() - startTime,
				}),
			).fire(CHANNEL_ID);
		}
	}, [cloudId, createAnalyticsEvent, shareClient]);

	return { config, fetchConfig };
};
